import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { ISecAdvCatalogue, ISecAdvClientType } from '../../api/sec-adv-contracts';
import { formatCount } from '../../components/sec-adv-values';
import { attributeAt, compactDollars, formatShare, part1AKey } from './register';

/**
 * The firm page's two hand-drawn charts (SR-423) — no chart library: a donut of AUM by client type
 * and a bar for a share. The categorical hues are the dataviz method's validated reference eight in
 * its fixed order; a firm's client types past the seventh fold into "Other", in a neutral grey, so
 * no hue is ever generated. Text wears text colours; a swatch beside it carries the identity, and
 * the legend under the donut is its table.
 */
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const OTHER = '#8a8f98';

/** One 5.D client type, named and coloured for this firm. */
export interface IClientType {
  /** `a` to `n`. */
  type: string;
  /** Short — "Individuals". */
  name: string;
  /** Form ADV's own words — "Individuals (other than high net worth individuals)". */
  fullName: string;
  /** 5.D(1); a "fewer than 5" with no count, or a count of 0, is 1 here, as RIA Pulse counts it. */
  count: number;
  /** The count as reported: "fewer than 5" kept apart from a number. */
  fewerThanFive: boolean;
  countReported: Nullable<number>;
  raum: Nullable<number>;
  description: Nullable<string>;
  color: string;
  /** Where the row's answers are in the filing. */
  source: string;
}

/** 5.D's rows with a name and a colour — the seven largest by AUM in the series' order, the rest
 *  grey. The same colours on Overview and Analytics. */
export function clientTypes(
  rows: Nullable<ISecAdvClientType[]>,
  catalogue: Nullable<ISecAdvCatalogue>
): IClientType[] {
  const answered = (rows ?? []).filter(
    (row) => (row.count ?? 0) > 0 || row.fewerThanFive || (row.raum ?? 0) > 0
  );
  const ranked = [...answered].sort(
    (a, b) => (b.raum ?? 0) - (a.raum ?? 0) || (b.count ?? 0) - (a.count ?? 0)
  );
  return answered.map((row) => {
    const rank = ranked.indexOf(row);
    const key = part1AKey(catalogue, `Q5D${row.type.toUpperCase()}1`);
    const label = key ? (attributeAt(catalogue, key)?.attribute.label ?? '') : '';
    const colon = label.indexOf(': ');
    const fullName = (colon === -1 ? label : label.slice(0, colon)) || `Client type 5.D(${row.type})`;
    return {
      type: row.type,
      name: CLIENT_TYPE_NAMES[row.type] ?? fullName,
      fullName,
      count: row.fewerThanFive && !row.count ? 1 : (row.count ?? 0),
      fewerThanFive: row.fewerThanFive,
      countReported: row.count,
      raum: row.raum,
      description: row.description,
      color: rank < SERIES.length ? SERIES[rank] : OTHER,
      source: key ?? 'FormInfo.Part1A.Item5D'
    };
  });
}

/** 5.D's rows in a few words; the form's own wording is the tooltip. */
const CLIENT_TYPE_NAMES: Record<string, string> = {
  a: 'Individuals',
  b: 'High net worth individuals',
  c: 'Banking or thrift institutions',
  d: 'Investment companies',
  e: 'Business development companies',
  f: 'Pooled investment vehicles',
  g: 'Pension and profit sharing plans',
  h: 'Charitable organizations',
  i: 'State or municipal government',
  j: 'Other investment advisers',
  k: 'Insurance companies',
  l: 'Sovereign wealth funds',
  m: 'Corporations or other businesses',
  n: 'Other'
};

/** A client type's short name, Form ADV's wording on hover. */
export function ClientTypeName({ type }: { type: IClientType }) {
  if (type.name === type.fullName) return <span className="min-w-0">{type.name}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="min-w-0 cursor-help outline-none">
          {type.name}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{type.fullName}</TooltipContent>
    </Tooltip>
  );
}

/** A client type's count as said: "Fewer than 5" where that is what the firm ticked. */
export function clientCount(type: IClientType): string {
  if (type.fewerThanFive && !type.countReported) return 'Fewer than 5';
  return type.countReported === null ? '–' : formatCount(type.countReported);
}

/** A swatch in a series' colour, beside the words that name it. */
export function Swatch({ color }: { color: string }) {
  return (
    <span aria-hidden className="inline-block size-2.5 shrink-0 rounded-sm" style={{ background: color }} />
  );
}

const SIZE = 168;
const STROKE = 26;

/** AUM by client type as a donut, its legend beside it; hovering a slice or a legend line reads it
 *  in the centre. */
export function ClientDonut({ types }: { types: IClientType[] }) {
  const slices = types.filter((type) => (type.raum ?? 0) > 0);
  const total = slices.reduce((sum, type) => sum + (type.raum ?? 0), 0);
  const [hover, setHover] = React.useState<Nullable<string>>(null);
  if (total === 0) return null;

  const radius = (SIZE - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  // A 2 px gap of the card's own colour between slices, where a slice is wide enough to keep one.
  const gap = slices.length > 1 ? 2 : 0;
  let offset = 0;
  const hovered = slices.find((type) => type.type === hover) ?? null;

  return (
    <div className="grid items-center gap-6 sm:grid-cols-[auto_minmax(0,1fr)]">
      <div className="relative shrink-0 justify-self-center" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label="Assets under management by client type"
          className="-rotate-90"
        >
          {slices.map((type) => {
            const length = ((type.raum ?? 0) / total) * circumference;
            const dash = Math.max(length - gap, 0.5);
            const slice = (
              <circle
                key={type.type}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={radius}
                fill="none"
                stroke={type.color}
                strokeWidth={hover === type.type ? STROKE + 4 : STROKE}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                opacity={hover === null || hover === type.type ? 1 : 0.45}
                onMouseEnter={() => setHover(type.type)}
                onMouseLeave={() => setHover(null)}
                className="transition-opacity"
              />
            );
            offset += length;
            return slice;
          })}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-content-center px-7 text-center">
          <span className="text-[0.6875rem] leading-tight text-muted-foreground">
            {hovered ? hovered.name : 'Total'}
          </span>
          <span className="text-sm font-semibold tabular-nums text-[var(--color-royal-blue)]">
            {compactDollars(hovered ? (hovered.raum ?? 0) : total)}
          </span>
          {hovered && (
            <span className="text-[0.6875rem] tabular-nums text-muted-foreground">
              {formatShare((hovered.raum ?? 0) / total)}
            </span>
          )}
        </div>
      </div>
      <ul aria-label="Client types by AUM" className="grid min-w-0 gap-1 text-xs">
        {[...slices]
          .sort((a, b) => (b.raum ?? 0) - (a.raum ?? 0))
          .map((type) => (
            <li
              key={type.type}
              onMouseEnter={() => setHover(type.type)}
              onMouseLeave={() => setHover(null)}
              className={cn(
                'flex min-w-0 items-center gap-2 rounded px-1 py-0.5',
                hover === type.type && 'bg-[var(--accent)]'
              )}
            >
              <Swatch color={type.color} />
              <span className="min-w-0 flex-1 break-words">
                <ClientTypeName type={type} />
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {compactDollars(type.raum ?? 0)} · {formatShare((type.raum ?? 0) / total)}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}

/** A share as a bar on a light track of its own colour. */
export function ShareBar({ share, color }: { share: number; color: string }) {
  return (
    <span
      aria-hidden
      className="block h-1.5 overflow-hidden rounded-full"
      style={{ background: `color-mix(in srgb, ${color} 18%, white)` }}
    >
      <span
        className="block h-full rounded-full"
        style={{ width: `${Math.min(100, Math.max(0, share * 100))}%`, background: color }}
      />
    </span>
  );
}
